/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-console */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { ReferenceCacheKeys, ReferenceKeys } from '../../types/types';
import { getReferences } from '../../api/user';

interface Option {
  label: string;
  value: string;
  id?: string;
  pid?: string;
}

type ReferenceData = Partial<Record<ReferenceKeys, Option[]>>;

interface ReferenceContextType {
  references: ReferenceData;
  loading: boolean;
}

const ReferenceContext = createContext<ReferenceContextType>({
  references: {},
  loading: true,
});

export const ReferenceProvider = ({ children }: { children: ReactNode }) => {
  const [references, setReferences] = useState<ReferenceData>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReferences = async () => {
      try {
        setLoading(true);
        const referenceKeys = Object.values(ReferenceKeys);
        const cacheKey = ReferenceCacheKeys.NEW_TICKET;

        const data = await getReferences(referenceKeys, cacheKey);
        const normalized: ReferenceData = {};

        data?.references?.forEach((ref: any) => {
          const key = ref.referenceKey.toLowerCase() as ReferenceKeys;

          if (!Object.values(ReferenceKeys).includes(key)) return;

          if (key === ReferenceKeys.OPEN_BY) {
            normalized[key] = ref.referenceDetails
              .filter((d: any) => d.employeeId)
              .map((d: any) => ({
                label: d.userName,
                value: d.employeeId,
              }));
          } else {
            normalized[key] = ref.referenceDetails.map((d: any) => ({
              label: d.description,
              value: d.referenceCode,
              ...(d.parentRefId && { pid: d.parentRefId }),
              ...(d.id && { id: d.id }),
            }));
          }
        });

        setReferences(normalized);
      } catch (error) {
        console.error('Error loading references:', error);
      } finally {
        setLoading(false);
      }
    };

    loadReferences();
  }, []);

  return (
    <ReferenceContext.Provider value={{ references, loading }}>
      {children}
    </ReferenceContext.Provider>
  );
};

export const useReferenceContext = () => useContext(ReferenceContext);
